import { createEffect, createEvent, createStore, sample } from 'effector';
import type {
  EventBusContext,
  EventBusRequest,
  SceneOpenEvent,
} from '../shared/eventBus';
import type {
  AssistantAction,
  AssistantMessageBlock,
  AssistantMessageLink,
  AssistantSuggestion,
} from '../types/assistant';
import type { Sight } from '../types';
import type { Locale } from '../shared/locale';
import type { SceneMarker } from '../shared/sceneContext';
import { publishEventBusEventSync } from '../services/assistantClient';
import type { EventBusMessage } from '../shared/eventBus';
import {
  $assistantSessionId,
  assistantStreamMessageReceived,
  assistantStreamSessionReceived,
} from './assistantSession';
import {
  $appState,
  $currentSight,
  setSceneId,
} from './appState';
import { $locale } from './locale';
import { assistantChatReset } from './assistantEvents';
import { $sceneStateSnapshots } from './sceneState';
import { KNOWN_SCENES, SCENE_POI_ID } from '../scene/sceneNavigationMap';
import { INSIDE_SCENE_PARENT } from '../scene/insideScenes/registry';
import { BACKEND_ERA_SCENE_IDS } from '../scene/eraScenes/registry';

/**
 * Scene Open flow.
 *
 * On every navigation to a content-bearing scene the client POSTs a
 * `scene_open` event to `/api/stream/sync` — backend's synchronous
 * endpoint that returns the `assistant_message` reply inline (no SSE
 * round-trip). The reply carries:
 *   - the scene intro card (in `message.content` / `message.blocks`)
 *   - the actual per-scene `tour: TourItem[]` (drives POI chips order
 *     + visited grayscale state)
 *
 * Backend contract:
 *   - `message.content === ""` means "no authored card for this scene"
 *     → do NOT render a chat bubble; still apply `tour[]`.
 *   - `scene_open` does NOT run OpenAI → no `thinking` events.
 *
 * The sync reply is fed back into `assistantStreamMessageReceived` so
 * the existing handler in `assistantHud.ts` processes it unchanged —
 * same path that previously consumed the SSE-delivered reply.
 *
 * Design choices:
 *
 *   1. `scene_open` requestIds live in a SEPARATE pool
 *      (`$pendingSceneOpenRequests`) from `$latestRequestId` /
 *      `$pendingRequestIds`. Reasons:
 *        - the user can interleave a `user_message` / `poi_click` while
 *          `scene_open` is still in flight — sharing `$latestRequestId`
 *          would let the user request overwrite it and the
 *          `scene_open` reply would be dropped by the
 *          `isMatchingRequestId` filter.
 *        - `assistantStopRequested` posts `cancel_generation` for every
 *          id in `$pendingRequestIds`. `scene_open` never runs OpenAI,
 *          so cancelling it would be a wasted POST.
 *
 *   2. `$pendingSceneOpenRequests: Map<requestId, sceneId>` keeps the
 *      `sceneId` snapshot at SEND time, not RECEIPT time. The sync
 *      response is so fast that drift is rare, but the snapshot is
 *      cheap insurance against a user navigating mid-flight.
 *
 *   3. The intro card is **buffered** until `sceneLoadingFinished` (the
 *      splat / GLB reveal completes). Adding it earlier means:
 *        - typewriter starts before HUD is even visible;
 *        - `assistantSkipAllAnimations()` (fired on scene transitions)
 *          would mark the freshly-added card `disableAnimation: true`,
 *          killing the intro typewriter for the next scene.
 *
 *   4. The chat input is BLOCKED while a `scene_open` is in flight
 *      (`$assistantWaitingSceneOpen`). With the sync endpoint the POST
 *      typically resolves in <1 s, so the block is barely visible.
 *
 *   5. `overall` is included in the whitelist (`'overall-scene'` per
 *      `SCENE_POI_ID['overall']`).
 *
 * Legacy: an SSE-gated POST flow on `/api/stream` existed previously.
 * It raced the Chrome SSE handshake (>8 s on mobile), losing replies.
 * The sync endpoint sidesteps that entirely.
 */

// --- Whitelist of poiFilterIds that backend supports for scene_open ---
//
// Built from KNOWN_SCENES (URL ids) mapped to their poiFilterIds via
// SCENE_POI_ID, plus all inside-scene ids (mapped the same way), plus
// backend-supported era scenes (BACKEND_ERA_SCENE_IDS — real chat/POI eras like the Bronze Age
// Kaaba; stub eras stay out and never fire scene_open).
//
// Adding a new top-level / inside / era scene? Add it to the upstream registry
// (sceneNavigationMap, insideScenes/registry or eraScenes/registry) — this set
// rebuilds automatically at module load.
const SCENE_OPEN_SUPPORTED_SCENE_IDS: ReadonlySet<string> = (() => {
  const ids = new Set<string>();
  for (const urlId of KNOWN_SCENES) {
    ids.add(SCENE_POI_ID[urlId] ?? urlId);
  }
  for (const insideId of Object.keys(INSIDE_SCENE_PARENT)) {
    ids.add(SCENE_POI_ID[insideId] ?? insideId);
  }
  for (const eraId of BACKEND_ERA_SCENE_IDS) {
    ids.add(SCENE_POI_ID[eraId] ?? eraId);
  }
  return ids;
})();

export const isSceneOpenSupported = (sceneId: string | number | null): boolean => {
  if (sceneId === null) return false;
  return SCENE_OPEN_SUPPORTED_SCENE_IDS.has(String(sceneId));
};

const createRequestId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `scene-open-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

// --- Events ---

export const sceneOpenRequested = createEvent<{ sceneId: string; requestId: string }>();

/**
 * Removes an in-flight scene_open requestId from the pending map.
 * Dispatched by `assistantHud.ts` after the corresponding assistant_message
 * has been processed (regardless of whether the card was empty or not).
 * Also dispatched on `publishSceneOpenFx.failData` to unblock the user
 * when the POST itself fails.
 */
export const sceneOpenResolved = createEvent<{ requestId: string }>();

export type BufferedSceneOpenMessage = {
  blocks: AssistantMessageBlock[];
  suggestions: AssistantSuggestion[];
  actions: AssistantAction[];
  links: AssistantMessageLink[];
  requestId: string;
};

/**
 * Buffers an intro card while the scene is still loading. Flushed by
 * `assistantHud.ts` on `sceneLoadingFinished` via
 * `sceneOpenMessageFlushed`.
 */
export const sceneOpenMessageBuffered = createEvent<BufferedSceneOpenMessage>();
export const sceneOpenMessageFlushed = createEvent();

// --- Per-scene intro cache (for non-chat surfaces) ---
//
// Non-chat surfaces (e.g. `OverallSceneOverlay` on the root scene) need
// the intro payload too — but they DON'T read `$assistantMessages` (no
// chat there). This store mirrors the latest scene_open reply for each
// sceneId so those surfaces can subscribe without going through the
// message-history channel.
//
// Lifetime: tab session. NOT reset on `assistantChatReset` (which fires
// on every navigation) — that would wipe the cache between scenes and
// cause an empty flash when returning to a previously-visited scene
// while its fresh scene_open round-trip is still in flight.

export type SceneOpenIntroPayload = {
  blocks: AssistantMessageBlock[];
  suggestions: AssistantSuggestion[];
  actions: AssistantAction[];
  links: AssistantMessageLink[];
};

export const sceneOpenIntroReceived = createEvent<{
  sceneId: string;
  intro: SceneOpenIntroPayload;
}>();

export const $sceneOpenIntros = createStore<Map<string, SceneOpenIntroPayload>>(
  new Map(),
).on(sceneOpenIntroReceived, (map, { sceneId, intro }) => {
  const next = new Map(map);
  next.set(sceneId, intro);
  return next;
});

// --- Stores ---

export const $pendingSceneOpenRequests = createStore<Map<string, string>>(new Map())
  .on(sceneOpenRequested, (map, { sceneId, requestId }) => {
    const next = new Map(map);
    next.set(requestId, sceneId);
    return next;
  })
  .on(sceneOpenResolved, (map, { requestId }) => {
    if (!map.has(requestId)) return map;
    const next = new Map(map);
    next.delete(requestId);
    return next;
  })
  .reset(assistantChatReset);

export const $bufferedSceneOpenMessage = createStore<BufferedSceneOpenMessage | null>(null)
  .on(sceneOpenMessageBuffered, (_, payload) => payload)
  .on(sceneOpenMessageFlushed, () => null)
  .reset(assistantChatReset);

/**
 * UI gate: true while any `scene_open` request has not yet resolved
 * (resolved = backend replied AND card was either added to chat or
 * dropped as empty). Drives `ChatInput.disabled`.
 */
export const $assistantWaitingSceneOpen = $pendingSceneOpenRequests.map(
  (map) => map.size > 0,
);

// --- POST flow ---

export const buildEventBusContext = (options: {
  currentSight: Sight | null;
  markers: SceneMarker[];
  locale: Locale;
  sceneId: string | number | null;
}): EventBusContext => {
  const markerDetails = options.markers.map((m) => ({
    sightId: m.sightId,
    visible: m.visible,
    occluded: m.occluded,
    distance: Number.isFinite(m.distance) ? Number(m.distance) : null,
  }));
  return {
    locale: options.locale,
    sceneId: options.sceneId ?? 'main',
    currentSight: options.currentSight?.id ?? null,
    markers: markerDetails,
  };
};

// --- Synchronous POST → `/api/stream/sync` ---
//
// Backend returns the `assistant_message` reply inline (single
// request/response). No SSE round-trip, no handshake timing race —
// works the same on every browser and network. This replaced the
// earlier SSE-gated flow which hit a "slow Chrome handshake → orphaned
// reply" failure on mobile.
//
// The sync reply is fed back into `assistantStreamMessageReceived` so
// the existing `sceneOpenResponseProcessed` handler in `assistantHud.ts`
// processes it without changes — same path as the legacy SSE-delivered
// reply, just dispatched from a different source.

export const publishSceneOpenFx = createEffect<EventBusRequest, EventBusMessage>(
  async (payload) => publishEventBusEventSync(payload),
);

sample({
  clock: sceneOpenRequested,
  source: {
    currentSight: $currentSight,
    sessionId: $assistantSessionId,
    locale: $locale,
    appState: $appState,
  },
  fn: (source, { sceneId, requestId }): EventBusRequest => {
    const event: SceneOpenEvent = {
      type: 'scene_open',
      sceneId,
      requestId,
    };
    return {
      sessionId: source.sessionId ?? undefined,
      responseFormat: 'markdown',
      context: buildEventBusContext({
        currentSight: source.currentSight,
        // Sent before the new scene has markers: `sceneLoadingReset` has just
        // cleared the getter.
        markers: [],
        locale: source.locale,
        sceneId: source.appState.sceneId,
      }),
      event,
    };
  },
  target: publishSceneOpenFx,
});

// Feed the sync reply into the SSE-message channel — same handler as
// for the legacy stream-delivered reply.
sample({
  clock: publishSceneOpenFx.doneData,
  target: assistantStreamMessageReceived,
});

// Backend may rotate sessionId via the reply envelope; forward it so
// other flows (`user_message` POSTs, SSE reconnects) stay aligned.
sample({
  clock: publishSceneOpenFx.doneData,
  filter: (msg) => typeof msg.sessionId === 'string' && msg.sessionId.length > 0,
  fn: (msg) => msg.sessionId,
  target: assistantStreamSessionReceived,
});

// If the POST itself fails (network / 5xx / abort), drain the entire
// pending map to unblock the chat input. `createEffect.failData` doesn't
// carry the original params; in practice we only have one scene_open
// in flight, so blanket resolve is safe.
publishSceneOpenFx.failData.watch(() => {
  const map = $pendingSceneOpenRequests.getState();
  for (const requestId of map.keys()) {
    sceneOpenResolved({ requestId });
  }
});

assistantChatReset.watch(() => {
  // No timers / queue to clean up in the sync flow; kept as an empty
  // hook for symmetry with previous code (and an obvious extension
  // point if we later need per-request abort).
});

// --- Navigation triggers ---
//
// Single source of navigation events: `setSceneId` fires on every
// `enterScene`, `goBack`, `rebuildStack` (see sceneNavigation.ts samples).
// Using the underlying event (rather than the derived `$sceneId` store)
// avoids Effector's `.map` deduplication — re-entering the same scene
// MUST trigger a fresh scene_open.
//
// Two sequenced samples on the SAME clock:
//   (a) assistantChatReset FIRST  (so the new scene starts with a clean
//       chat, and any stale pending scene_open / buffered card from the
//       previous scene is wiped).
//   (b) sceneOpenRequested SECOND (only for whitelisted scenes).
//
// Effector guarantees sample handlers fire in registration order on the
// same clock. The chat reset MUST come first because $pendingSceneOpenRequests
// is reset by assistantChatReset — if we ran them in reverse, the new
// requestId would get registered and then immediately wiped.

const navigationDetected = createEvent<string>();

sample({
  clock: setSceneId,
  filter: (sceneId): sceneId is string | number => sceneId !== null,
  fn: (sceneId): string => String(sceneId),
  target: navigationDetected,
});

sample({
  clock: navigationDetected,
  target: assistantChatReset,
});

// Если для возвращаемой main-сцены есть snapshot (kaaba → kaaba-inside →
// goBack → kaaba), backend intro будет дубликатом восстановленного
// сообщения. Поэтому при наличии snapshot пропускаем scene_open POST.
// Snapshot НЕ удаляется на consume — фильтр читает его в этой же
// транзакции, ПРЕЖДЕ чем `sceneSnapshotConsumed` (clock —
// assistantChatReset, регистрируется ПОЗЖЕ этого sample-а в порядке
// импорта). Если порядок изменится — фильтр сломается; защита: filter
// читает source синхронно через Effector, а sceneSnapshotConsumed.on
// для $sceneStateSnapshots отсутствует (мы не удаляем).
// См. docs/architecture/scene-registry.md → «Scene state restore on goBack»
// и .claude/rules/effector-order.md.
sample({
  clock: navigationDetected,
  source: $sceneStateSnapshots,
  filter: (snapshots, sceneId) =>
    isSceneOpenSupported(sceneId) && !snapshots.has(sceneId),
  fn: (_snapshots, sceneId) => ({ sceneId, requestId: createRequestId() }),
  target: sceneOpenRequested,
});
